import { useEffect, useRef, useState } from "react";

import {
  createTableShareFile,
  createTableShareLink,
  exportFormTables,
  getFormPreviewByWorkflowUser,
  processSelectTag,
  saveFormData,
  TableExportFormat,
  TableExportPayload,
  TableShareMode,
  uploadFile
} from "@/api/form";
import { getNodeSequence, nodeStateMove } from "@/api/workflow";
import "@majidh1/jalalidatepicker";
import "@majidh1/jalalidatepicker/dist/jalalidatepicker.min.css";
import { digitsFaToEn, numberToWords } from "@persian-tools/persian-tools";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { createRoot } from "react-dom/client";
import toast from "react-hot-toast";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { MoonLoader } from "react-spinners";

import { ApiResult } from "@/types/api-response";
import {
  AccessAction,
  AccessRuleMode,
  AccessRuleStatus
} from "@/types/table-access";
import { NodeSequenceType } from "@/types/workflow/nodeSequence";

import { useActiveRoleStore } from "@/hooks/store/use-active-role-store";

import { TableAccessManager } from "@/utils/table-access-manager";

import TableExportScopeModal, {
  ExportScopeResult,
  ExportScopeTableItem
} from "@/pages/form/final/table-export-scope-modal";
import TableShareConfigModal, {
  TableShareConfigType,
  TableShareConfigValue
} from "@/pages/form/final/table-share-config-modal";
import TableShareModal, {
  TableShareAction
} from "@/pages/form/final/table-share-modal";

import { PageAdvancedSearchModal } from "@/components/widget/editor/table-actions/page-advanced-search-modal";
import Align from "@/components/widget/editor/toolbar-buttons/aligns";
import Bold from "@/components/widget/editor/toolbar-buttons/bold";
import FontPicker from "@/components/widget/editor/toolbar-buttons/font-picker";
import FontSizePicker from "@/components/widget/editor/toolbar-buttons/font-size";
import InsertHorizonLine from "@/components/widget/editor/toolbar-buttons/insert-horizon-line";
import InsertImage from "@/components/widget/editor/toolbar-buttons/insert-image";
import Italic from "@/components/widget/editor/toolbar-buttons/italic";
import ListOrder from "@/components/widget/editor/toolbar-buttons/list-order";
import Strikethrough from "@/components/widget/editor/toolbar-buttons/strikethrough";
import TextColorPicker from "@/components/widget/editor/toolbar-buttons/text-color";
import TextHighlight from "@/components/widget/editor/toolbar-buttons/text-highlight";
import Underline from "@/components/widget/editor/toolbar-buttons/underline";

import NodeSequenceCircles from "../_components/final/nodeSequenceStepper";
import TableExportModal from "./table-export-modal";

declare global {
  interface Window {
    jalaliDatepicker: any;
  }
}

type TableSortState = {
  id: string;
  sortElement: string;
  sortDirection: "asc" | "desc";
};

type TablePaginationState = {
  id: string;
  pageNumber: string;
  pageSize: number;
};

type ExportCollectOptions = {
  selectedColumnsByTable?: Record<string, string[]>;
  rowCount?: number;
};

const EditorComponent = ({ id = "" }) => {
  const editorRef = useRef(null);

  return (
    <div className="flex h-full flex-col border p-2">
      <div className="mb-2 flex items-center justify-end gap-x-2 border-b pb-2">
        <FontPicker editorRef={editorRef} />
        <FontSizePicker editorRef={editorRef} />
        <Bold editorRef={editorRef} />
        <Italic editorRef={editorRef} />
        <Underline editorRef={editorRef} />
        <Strikethrough editorRef={editorRef} />
        <TextColorPicker editorRef={editorRef} />
        <TextHighlight editorRef={editorRef} />
        <Align editorRef={editorRef} />
        <ListOrder editorRef={editorRef} />
        <InsertHorizonLine editorRef={editorRef} />
        <InsertImage editorRef={editorRef} />
      </div>
      <div
        id={id}
        ref={editorRef}
        contentEditable
        className="editor-content flex-1 outline-none"
      ></div>
    </div>
  );
};

const FormFinal = () => {
  const { workflowUserId } = useParams<{ workflowUserId: string }>();
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement | null>(null);
  const formEventsAbortControllerRef = useRef<AbortController | null>(null);
  const attachEventListenersRef = useRef<() => void>(() => {});
  const { activeRoleId } = useActiveRoleStore();
  const numericActiveRoleId = activeRoleId ? parseInt(activeRoleId, 10) : 0;
   
  const [lastFormData, setLastFormData] = useState("");
  const [tempData, setLastTempData] = useState("");

  const [tablePagination, setTablePagination] = useState<
    TablePaginationState[] | []
  >([]);

  const [tableSearch, setTableSearch] = useState<
    { id: string; searchElement: string; searchValue: string }[] | []
  >([]);
  const [tableSort, setTableSort] = useState<TableSortState[] | []>([]);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportScopeModalOpen, setIsExportScopeModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportTableCount, setExportTableCount] = useState(0);
  const [pendingExportFormat, setPendingExportFormat] =
    useState<TableExportFormat | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareTableCount, setShareTableCount] = useState(0);
  const [pendingShareTables, setPendingShareTables] = useState<
    TableExportPayload[]
  >([]);
  const [exportScopeContext, setExportScopeContext] = useState<
    "export" | "share"
  >("export");
  const [isShareConfigModalOpen, setIsShareConfigModalOpen] = useState(false);
  const [shareConfigType, setShareConfigType] =
    useState<TableShareConfigType | null>(null);
  const [pendingShareAction, setPendingShareAction] =
    useState<TableShareAction | null>(null);
  const [currentAdvancedSearchTableId, setCurrentAdvancedSearchTableId] =
    useState<string>("");
  const [advancedSearchTableFields, setAdvancedSearchTableFields] = useState<
    Array<{ id: string; name: string }>
  >([]);

  const [formInputs, setFormInputs] = useState<
    { tableId: number; lable: string; value?: string }[] | null
  >(null);

  const formInputsRef = useRef(formInputs);
  const [nodeSequence, setNodeSequence] = useState<ApiResult<
    NodeSequenceType[]
  > | null>(null);

  const location = useLocation();

  useEffect(() => {
    const locationState = location.state as any;
    if (locationState?.formInputs && Array.isArray(locationState.formInputs)) {
      setFormInputs(locationState.formInputs);
    } else {
      setFormInputs(null);
    }
  }, [workflowUserId, location.state]);

  useEffect(() => {
    formInputsRef.current = formInputs;
  }, [formInputs]);

  const {
    data: form,
    isLoading,
    refetch
  } = useQuery({
    queryKey: [
      "form-preview",
      workflowUserId,
      tablePagination,
      tableSearch,
      tableSort,
      formInputs
    ],
    queryFn: async () => {
      const data = await getFormPreviewByWorkflowUser(
        workflowUserId!,
        tableSearch,
        tablePagination,
        tableSort,
        formInputs
      );
      const nodeSequenceResponse = await getNodeSequence(workflowUserId!);
      setNodeSequence(nodeSequenceResponse);
      return data;
    }
  });
  const formBody = form?.data.htmlFormBody ?? "";
  useEffect(() => {
    if (form && form.data && tempData) {
      if (tempData.length !== formBody?.length) {
        setLastFormData(tempData);
      }
      setLastTempData(formBody);
    } else if (form && form.data) {
      setLastTempData(formBody);
    }
  }, [form, tempData]);

  const { mutate } = useMutation({
    mutationFn: ({
      state,
      nodeId,
      newWorkflowUserId,
      edgeIndex,
      formDataInput
    }: {
      state: number;
      nodeId?: string | null;
      newWorkflowUserId?: string | null;
      edgeIndex?: number | null;
      formDataInput?: {
        FormInputs: { tableId: number; lable: string; value?: string }[];
      };
    }) => {
      return nodeStateMove(
        workflowUserId!,
        state,
        nodeId,
        newWorkflowUserId,
        edgeIndex,
        formDataInput
      );
    },
    onSuccess: (data, variables) => {
      if (workflowUserId! === data?.data) {
        if (variables.formDataInput) {
          setFormInputs([...(variables.formDataInput.FormInputs ?? [])]);
        }
      } else {
        navigate(`/dashboard/workflow-form/${data?.data}`, {
          state: {
            tabTitle: "فرم گردش کار",
            tabPath: `/dashboard/workflow-form/${data?.data}`,
            formInputs: variables.formDataInput?.FormInputs ?? formInputs ?? []
          }
        });
      }
    }
  });

  const printFormContainer = () => {
    const formContainer = document.getElementById("formContainer");
    if (!formContainer) return false;

    const printWindow = window.open("", "", "height=600,width=800");
    if (!printWindow) return false;

    printWindow.document.write(
      '<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body>'
    );
    printWindow.document.write(formContainer.innerHTML);
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.print();

    return true;
  };

  const getElementTokenValue = (
    elementId: string,
    searchRoot: ParentNode | null
  ) => {
    const scopedElement = searchRoot?.querySelector(
      `#${CSS.escape(elementId)}`
    ) as
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement
      | HTMLElement
      | null;
    const element = scopedElement ?? document.getElementById(elementId);

    if (!element) return null;

    const anyElement = element as HTMLInputElement & {
      value?: string;
    };

    if (typeof anyElement.value !== "undefined") {
      return anyElement.value ?? "";
    }

    return element.textContent ?? "";
  };

  const resolveDataFilterTokens = (
    dataFilter: string,
    searchRoot: ParentNode | null
  ) => {
    return dataFilter.replace(/\{\{Element:(\d+)\}\}/g, (match, elementId) => {
      const tokenValue = getElementTokenValue(elementId, searchRoot);
      return tokenValue === null ? match : tokenValue;
    });
  };

  const handleDataFormActionSelectPointerDown = async (event: Event) => {
    const pe = event as PointerEvent;
    const target = (pe.target as HTMLElement) || null;
    const select = target?.closest(
      "select[data-form-action]"
    ) as HTMLSelectElement | null;

    if (!select) return;
    if (select.getAttribute("data-form-action-loaded") === "true") return;
    if (select.getAttribute("data-form-action-loading") === "true") return;

    select.setAttribute("data-form-action-loading", "true");

    try {
      let htmlBodyToSend = select.outerHTML;
      const dataFilter = select.getAttribute("data-filter") ?? "";

      if (dataFilter) {
        const replaced = resolveDataFilterTokens(dataFilter, formRef.current);
        if (replaced !== dataFilter) {
          const clone = select.cloneNode(true) as HTMLSelectElement;
          clone.setAttribute("data-filter", replaced);
          htmlBodyToSend = clone.outerHTML;
        }
      }

      const response = await processSelectTag({
        htmlBody: htmlBodyToSend,
        userId: 0,
        formInputs: formInputsRef.current ?? []
      });

      const processedHtml = response?.data ?? "";
      if (!processedHtml.trim()) return;

      const template = document.createElement("template");
      template.innerHTML = processedHtml.trim();
      const replacement = template.content.firstElementChild;

      // داخل handleDataFormActionSelectPointerDown بعد از replacement
      if (replacement instanceof HTMLSelectElement) {
        replacement.removeAttribute("data-form-action-loading");
        replacement.setAttribute("data-form-action-loaded", "true");
        select.replaceWith(replacement);

        // تبدیل select جدید به searchable با گزینه "هیچکدام"
        convertSelectToSearchableWithNoneOption(replacement);
      } else {
        select.innerHTML = processedHtml.trim();
        select.removeAttribute("data-form-action-loading");
        select.setAttribute("data-form-action-loaded", "true");

        // بازسازی dropdown برای select آپدیت شده
        const wrapper = select.closest(".searchable-select-wrapper");
        if (wrapper) {
          const parent = wrapper.parentNode;
          wrapper.remove();
          if (parent) {
            parent.appendChild(select);
            convertSelectToSearchableWithNoneOption(select);
          }
        }
      }
    } catch {
      toast.error("خطا در دریافت گزینه‌های فرم");
    } finally {
      select.removeAttribute("data-form-action-loading");
      select.setAttribute("data-form-action-loaded", "true");
    }
  };

  const handleButtonClickWithAction = async (e: MouseEvent) => {
    const target = e.currentTarget as HTMLButtonElement;
    const activeFormRoot =
      (target.closest(".prose-preview") as HTMLDivElement | null) ??
      formRef.current;
    const action = target.getAttribute("data-action");
    const apiUrl = target.getAttribute("data-api")?.trim() ?? null;
    const apiMethod =
      (target.getAttribute("data-method") as "get" | "post") ?? null;

    const edgeIndexStr = target.getAttribute("data-edge-index");
    const edgeIndex = edgeIndexStr ? parseInt(edgeIndexStr) : null;

    const collectedFormInputs: {
      tableId: number;
      lable: string;
      value?: string;
    }[] = [];

    if (action === "print") {
      if (!printFormContainer()) {
        toast.error("امکان چاپ در حال حاضر وجود ندارد");
      }
      return;
    }

    const nodeId = null;
    const newWorkflowUserId = null;
    let formDataInput:
      | { FormInputs: { tableId: number; lable: string; value?: string }[] }
      | undefined = undefined;
    const state =
      action === "next-node"
        ? 1
        : action === "previous-node"
          ? 2
          : action === "cancel"
            ? 3
            : 5;

    if (state === 1 || state === 2) {
      const workflowUserData = target.getAttribute("data-workflow-user");
      const parts = workflowUserData?.split("@");
      const previousFormInputs = formInputs || [];
      formDataInput = {
        FormInputs: [
          ...previousFormInputs,
          ...collectedFormInputs // ★ اضافه کردن عناصر readonly
        ]
      };
      parts?.forEach((part) => {
        if (part && part.includes("-")) {
          const [tableId, Id] = part.split("-");
          const tableName = target.getAttribute("data-table-name") || "";
          formDataInput?.FormInputs.push({
            tableId: parseInt(Id.split(":")[1]),
            lable: tableName,
            value: tableId.split(":")[1]
          });
        }
      });
    }

    const formData: { id: number; content: string; group?: string }[] = [];

    let allFieldsFilled = true;
    const fileUploadPromises: Promise<void>[] = [];

    const formatSelectValueForSave = (value: string) => {
      const normalizedValue = value.trim();
      const compactMatch = normalizedValue.match(/^(\d+)-(\d+)$/);
      if (compactMatch) {
        return `Id:${compactMatch[1]} -TableId:${compactMatch[2]}`;
      }
      return normalizedValue;
    };

    if (activeFormRoot) {
      activeFormRoot.querySelectorAll("input").forEach((item) => {
        if (item.getAttribute("data-editor") != "true") {
          if (item.hasAttribute("required") && !item.value.trim()) {
            item.parentElement!.style.borderColor = "red";
            allFieldsFilled = false;
          } else {
            if (item.hasAttribute("data-regex")) {
              const regex = new RegExp(item.getAttribute("data-regex") ?? "");
              if (!regex.test(item.value.trim())) {
                toast.error(item.getAttribute("data-regex-message"));
                item.parentElement!.style.borderColor = "red";
                allFieldsFilled = false;
                item.parentElement
                  ?.querySelector("#input-error-message")
                  ?.remove();
                item.parentElement?.insertAdjacentHTML(
                  "beforeend",
                  `<span id="input-error-message">${item.getAttribute("data-regex-message") ?? "خطا در نوشتار"}</span>`
                );
                return;
              }
            }
            item.parentElement!.style.borderColor = "#cbd5e1";
            item.parentElement?.querySelector("#input-error-message")?.remove();
            const newItem: { id: number; content: string; group?: string } = {
              id: +item.id!,
              content: item.value
            };

            if (
              item.type === "file" &&
              item.getAttribute("data-editor") != "true"
            ) {
              if (item.files && item.files.length > 0) {
                const file = item.files[0];
                const allowedExtensions =
                  item.getAttribute("data-allowed-extensions") || undefined;
                const uploadPromise = uploadFile(file, allowedExtensions).then(
                  (result) => {
                    if (result?.fileUrl) {
                      newItem.content = result.fileUrl;
                    }
                  }
                );
                fileUploadPromises.push(uploadPromise);
              }
            }

            const inputGroup = item.getAttribute("data-group");
            if (inputGroup) newItem.group = inputGroup;
            if (item.id === "table-search-input") {
              return;
            }
            if (item.hasAttribute("data-readonly")) {

              collectedFormInputs.push({
                tableId: +item.id,
                lable: "properties",
                value: item.value
              });
              return;
            }
            formData.push(newItem);
          }
        }
      });

      activeFormRoot.querySelectorAll("select").forEach((item) => {
        if (item.hasAttribute("required") && !item.value.trim()) {
          item.parentElement!.style.borderColor = "red";
          allFieldsFilled = false;
        } else {
          item.parentElement!.style.borderColor = "#cbd5e1";
          const newItem: { id: number; content: string; group?: string } = {
            id: +item.id!,
            content: formatSelectValueForSave(item.value)
          };
          const selectGroup = item.getAttribute("data-group");
          if (selectGroup) newItem.group = selectGroup;
          if (item.id === "table-filter" || item.id === "table-sort-select") {
            return;
          }
          if (item.hasAttribute("data-readonly")) {
            console.log("dddddddddddd", item)

            collectedFormInputs.push({
              tableId: +item.id,
              lable: "properties",
              value: item.value
            });
            return;
          }
          formData.push(newItem);
        }
      });

      activeFormRoot.querySelectorAll("textarea").forEach((item) => {
        if (item.getAttribute("data-editor") != "true") {
          if (item.hasAttribute("required") && !item.value.trim()) {
            item.parentElement!.style.borderColor = "red";
            allFieldsFilled = false;
          } else {
            if (item.hasAttribute("data-regex")) {
              const regex = new RegExp(item.getAttribute("data-regex") ?? "");
              if (!regex.test(item.value.trim())) {
                toast.error(item.getAttribute("data-regex-message"));
                item.parentElement!.style.borderColor = "red";
                allFieldsFilled = false;
                item.parentElement
                  ?.querySelector("#input-error-message")
                  ?.remove();
                item.parentElement?.insertAdjacentHTML(
                  "beforeend",
                  `<span id="input-error-message">${item.getAttribute("data-regex-message") ?? "خطا در نوشتار"}</span>`
                );
                return;
              }
            }
            item.parentElement!.style.borderColor = "#cbd5e1";
            item.parentElement?.querySelector("#input-error-message")?.remove();
            const newItem: { id: number; content: string; group?: string } = {
              id: +item.id!,
              content: item.value
            };
            const inputGroup = item.getAttribute("data-group");
            if (inputGroup) newItem.group = inputGroup;
            if (item.id === "table-search-input") {
              return;
            }
            if (item.hasAttribute("data-readonly")) {

              collectedFormInputs.push({
                tableId: +item.id,
                lable: "properties",
                value: item.value
              });
              return;
            }
            formData.push(newItem);
          }
        }
      });

      activeFormRoot.querySelectorAll(".editor-content").forEach((item) => {
        if (item.hasAttribute("required")) {
          item.parentElement!.style.borderColor = "red";
          allFieldsFilled = false;
        } else {
          item.parentElement!.style.borderColor = "#cbd5e1";
          const newItem: { id: number; content: string; group?: string } = {
            id: +item.id!,
            content: item.innerHTML
          };
          const selectGroup = item.getAttribute("data-group");
          if (selectGroup) newItem.group = selectGroup;
          formData.push(newItem);
        }
      });
    }

    if (!allFieldsFilled) {
      toast.error("مقادیر اجباری رو پر کنید");
      return;
    }

    if (collectedFormInputs.length > 0) {
      formDataInput?.FormInputs.push(...collectedFormInputs);
    }

    try {
      if (fileUploadPromises.length > 0) {
        await Promise.all(fileUploadPromises);
      }

      const normalizedFormData = formData.filter(
        (item) => Number.isFinite(item.id) && item.id > 0
      );

      if (apiUrl) {
        await axios[apiMethod](apiUrl);
      }
      if (state !== 3) {
        const saveResponse = await saveFormData(
          workflowUserId!,
          numericActiveRoleId,
          normalizedFormData,
          [...(formInputs || []), ...(formDataInput?.FormInputs || [])]
        );

        if (
          saveResponse?.data &&
          Array.isArray(saveResponse.data) &&
          saveResponse.data.length > 0
        ) {
          const savedFormInputs = saveResponse.data.map((item: any) => ({
            tableId: item.tableId,
            lable: item.lable,
            value: item.value
          }));
          formDataInput = {
            FormInputs: [
              ...(formInputs || []),
              ...(formDataInput?.FormInputs || []),
              ...collectedFormInputs, // ★ اضافه کردن عناصر readonly
              ...savedFormInputs
            ]
          };
        }
      }

      // برای دکمه انصراف، اول منتظر mutate بمون بعد refetch کن
      if (state === 3) {
        await new Promise<void>((resolve, reject) => {
          mutate(
            {
              state,
              nodeId,
              newWorkflowUserId,
              formDataInput,
              edgeIndex
            },
            {
              onSuccess: () => {
                resolve();
              },
              onError: (error) => {
                reject(error);
              }
            }
          );
        });
        await refetch();
      } else {
        mutate({
          state,
          nodeId,
          newWorkflowUserId,
          formDataInput,
          edgeIndex
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const normalizeCellText = (cell: Element) =>
    (cell.textContent ?? "").replace(/\s+/g, " ").trim();

  const normalizeColumnKey = (value: string) => {
    const normalized = value.replace(/\s+/g, " ").trim();
    if (!normalized) return "";

    const asIndex = normalized.toUpperCase().indexOf(" AS ");
    const cleaned =
      asIndex >= 0 ? normalized.slice(0, asIndex).trim() : normalized;

    const stripped = cleaned
      .replace(/\[/g, "")
      .replace(/\]/g, "")
      .replace(/"/g, "")
      .replace(/"/g, "")
      .replace(/'/g, "")
      .trim();

    if (stripped === "*") return stripped;

    return stripped.includes(".")
      ? (stripped.split(".").pop()?.trim() ?? "")
      : stripped;
  };

  const getExportShareHeaders = (
    headers: string[],
    accessControlConfig: string | null,
    roleId: number
  ) => {
    if (!accessControlConfig || headers.length === 0) return headers;

    const parsed = TableAccessManager.deserialize(accessControlConfig);
    if (!parsed) return headers;

    const rules = parsed.rules.filter(
      (rule) => rule.status === AccessRuleStatus.EXPORT_SHARE
    );
    if (rules.length === 0) return headers;

    const headerMap = headers.map((header) => ({
      header,
      key: normalizeColumnKey(header)
    }));

    const hasAnyShowRule = rules.some(
      (rule) => rule.action === AccessAction.SHOW
    );
    let matchedAnyRule = false;
    let showAll = false;
    let hideAll = false;
    const visibleColumns = new Set<string>();
    const hiddenColumns = new Set<string>();

    rules.forEach((rule) => {
      if (rule.mode !== AccessRuleMode.ROLE) return;

      const roleMatch =
        rule.allRoles || (roleId > 0 && rule.roleIds.includes(roleId));
      if (!roleMatch) return;

      matchedAnyRule = true;

      const normalizedRuleColumns = (rule.columns ?? [])
        .map(normalizeColumnKey)
        .filter(Boolean);

      if (normalizedRuleColumns.includes("*")) {
        if (rule.action === AccessAction.HIDE) hideAll = true;
        if (rule.action === AccessAction.SHOW) showAll = true;
        return;
      }

      if (rule.action === AccessAction.HIDE) {
        normalizedRuleColumns.forEach((col) => hiddenColumns.add(col));
      } else {
        normalizedRuleColumns.forEach((col) => visibleColumns.add(col));
      }
    });

    if (hasAnyShowRule && !matchedAnyRule) return [];
    if (hideAll) return [];

    if (showAll) {
      return headerMap
        .filter((item) => !hiddenColumns.has(item.key))
        .map((item) => item.header);
    }

    if (visibleColumns.size > 0) {
      return headerMap
        .filter((item) => visibleColumns.has(item.key))
        .filter((item) => !hiddenColumns.has(item.key))
        .map((item) => item.header);
    }

    return headerMap
      .filter((item) => !hiddenColumns.has(item.key))
      .map((item) => item.header);
  };

  const collectTableScopeItems = (): ExportScopeTableItem[] => {
    if (!formRef.current) return [];

    const containers = Array.from(
      formRef.current.querySelectorAll("#table-container")
    );

    return containers
      .map<ExportScopeTableItem | null>((container, index) => {
        const table = container.querySelector("table");
        if (!table) return null;

        const headerRow =
          table.querySelector("thead tr") ?? table.querySelector("tr");
        if (!headerRow) return null;

        const headers = Array.from(headerRow.querySelectorAll("th, td"))
          .map(normalizeCellText)
          .filter((header) => header !== "");

        if (headers.length === 0) return null;

        const accessControlConfig =
          table.getAttribute("data-access-control") ?? null;
        const filteredHeaders = getExportShareHeaders(
          headers,
          accessControlConfig,
          numericActiveRoleId
        );

        if (filteredHeaders.length === 0) return null;

        return {
          tableId: table.id || `table-${index + 1}`,
          title: table.id ? `جدول ${table.id}` : `جدول ${index + 1}`,
          headers: filteredHeaders
        };
      })
      .filter((item): item is ExportScopeTableItem => item !== null);
  };

  const collectTablesForExport = (
    options?: ExportCollectOptions
  ): TableExportPayload[] => {
    if (!formRef.current) return [];

    return collectTablesFromRoot(formRef.current, options);
  };

  function collectTablesFromRoot(
    root: ParentNode,
    options?: ExportCollectOptions
  ): TableExportPayload[] {
    const containers = Array.from(
      (
        root as ParentNode & {
          querySelectorAll: typeof document.querySelectorAll;
        }
      ).querySelectorAll("#table-container")
    );

    return containers
      .map<TableExportPayload | null>((container, index) => {
        const table = container.querySelector("table");
        if (!table) return null;

        const headerRow =
          table.querySelector("thead tr") ?? table.querySelector("tr");
        if (!headerRow) return null;

        const headers = Array.from(headerRow.querySelectorAll("th, td"))
          .map(normalizeCellText)
          .filter((header) => header !== "");

        if (headers.length === 0) return null;

        const tableId = table.id || `table-${index + 1}`;
        const selectedColumns = options?.selectedColumnsByTable?.[
          tableId
        ]?.filter((column) => headers.includes(column)) ?? [...headers];

        const rowsSource = table.querySelectorAll("tbody tr");
        const bodyRows =
          rowsSource.length > 0
            ? Array.from(rowsSource)
            : Array.from(table.querySelectorAll("tr")).slice(1);

        const rows = bodyRows
          .map((row) =>
            Array.from(row.querySelectorAll("th, td"))
              .map(normalizeCellText)
              .slice(0, headers.length)
          )
          .map((row) => {
            const normalized = [...row];
            while (normalized.length < headers.length) normalized.push("");
            return normalized;
          })
          .filter((row) => row.some((cell) => cell !== ""));

        const accessControlConfig =
          table.getAttribute("data-access-control") ?? undefined;

        return {
          tableId,
          title: table.id ? `جدول ${table.id}` : `جدول ${index + 1}`,
          headers,
          rows,
          selectedColumns,
          accessControlConfig,
          exportRowCount: options?.rowCount
        };
      })
      .filter((item): item is TableExportPayload => item !== null);
  }

  const collectTablesFromScopedPreview = async (scope: ExportScopeResult) => {
    if (!formRef.current) return [];

    const previewHtml = await getFormPreviewByWorkflowUser(
      workflowUserId!,
      tableSearch,
      tablePagination,
      tableSort,
      formInputs,
      scope.mode === "limit" ? scope.rowCount : undefined,
      scope.mode === "all"
    );

    if (!previewHtml?.data) {
      return collectTablesForExport({
        selectedColumnsByTable: scope.selectedColumnsByTable,
        rowCount: scope.mode === "limit" ? scope.rowCount : undefined
      });
    }

    const parsed = new DOMParser().parseFromString(
      previewHtml.data.htmlFormBody,
      "text/html"
    );

    return collectTablesFromRoot(parsed, {
      selectedColumnsByTable: scope.selectedColumnsByTable,
      rowCount: scope.mode === "limit" ? scope.rowCount : undefined
    });
  };

  const extractFileName = (contentDisposition: string | undefined) => {
    if (!contentDisposition) return null;
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {
        return utf8Match[1];
      }
    }
    const simpleMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return simpleMatch?.[1] ?? null;
  };

  const downloadTablesByFormat = async (
    format: TableExportFormat,
    tables: TableExportPayload[]
  ) => {
    const response = await exportFormTables({
      format,
      fileName: `workflow-${workflowUserId ?? "export"}`,
      tables
    });
    const contentType =
      (response.headers["content-type"] as string) ||
      "application/octet-stream";
    const fileName =
      extractFileName(response.headers["content-disposition"]) ||
      `table-export-${Date.now()}.${format}`;
    const blob = new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async (format: TableExportFormat) => {
    const tables = collectTablesForExport();
    if (tables.length === 0) {
      toast.error("جدولی برای خروجی پیدا نشد");
      return;
    }

    setPendingExportFormat(format);
    setExportScopeContext("export");
    setIsExportModalOpen(false);
    setIsExportScopeModalOpen(true);
  };

  const copyShareLink = async (link: string) => {
    if (!navigator.clipboard?.writeText) return false;
    try {
      await navigator.clipboard.writeText(link);
      return true;
    } catch {
      return false;
    }
  };

  const resolveWorkflowUserId = () => {
    return workflowUserId ?? "";
  };

  const createSecureShareLink = async ({
    mode,
    tables,
    config
  }: {
    mode: TableShareMode;
    tables: TableExportPayload[];
    config?: TableShareConfigValue;
  }) => {
    const resolvedWorkflowUserId = resolveWorkflowUserId();
    if (!resolvedWorkflowUserId) throw new Error("workflow user id is invalid");
    const result = await createTableShareLink({
      workflowUserId: resolvedWorkflowUserId,
      mode,
      tables,
      expirationTimeSpan: config?.expirationTimeSpan,
      maxUseCount: config?.maxUseCount
    });
    if (!result?.shareUrl) throw new Error("share link was not generated");
    return result;
  };

  const createSecureShareFile = async ({
    format,
    tables
  }: {
    format: TableExportFormat;
    tables: TableExportPayload[];
  }) => {
    const resolvedWorkflowUserId = resolveWorkflowUserId();
    if (!resolvedWorkflowUserId) throw new Error("workflow user id is invalid");
    const result = await createTableShareFile({
      workflowUserId: resolvedWorkflowUserId,
      mode: "permanent",
      format,
      fileName: `workflow-${workflowUserId ?? "share"}`,
      tables
    });
    if (!result?.fileUrl) throw new Error("share file link was not generated");
    return result;
  };

  const shareWithSystem = async (shareUrl: string, shareText: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "اشتراک گذاری جدول",
          text: shareText,
          url: shareUrl
        });
        return "shared" as const;
      } catch {
        return "cancelled" as const;
      }
    }
    const copied = await copyShareLink(shareUrl);
    return copied ? ("copied" as const) : ("created" as const);
  };

  const executeShareAction = async (
    action: TableShareAction,
    tables: TableExportPayload[],
    config?: TableShareConfigValue
  ) => {
    const linkModeMap: Record<
      "link-permanent" | "link-expiring" | "link-multi-use" | "link-internal",
      TableShareMode
    > = {
      "link-permanent": "permanent",
      "link-expiring": "expiring",
      "link-multi-use": "multi-use",
      "link-internal": "internal"
    };

    setIsSharing(true);
    try {
      if (action in linkModeMap) {
        const mode = linkModeMap[action as keyof typeof linkModeMap];
        const linkResult = await createSecureShareLink({
          mode,
          tables,
          config
        });
        const copied = await copyShareLink(linkResult.shareUrl);
        toast.success(
          copied
            ? "لینک اشتراک گذاری امن ایجاد و کپی شد"
            : "لینک اشتراک گذاری امن ایجاد شد"
        );
        setIsShareModalOpen(false);
        return;
      }

      if (action.startsWith("export-")) {
        const format = action.replace("export-", "") as TableExportFormat;
        const fileResult = await createSecureShareFile({ format, tables });
        const copied = await copyShareLink(fileResult.fileUrl);
        if (copied) toast.success("لینک فایل اشتراکی ایجاد و کپی شد");
        else toast.success("فایل اشتراکی ایجاد شد");
        window.open(fileResult.fileUrl, "_blank", "noopener,noreferrer");
        setIsShareModalOpen(false);
        return;
      }

      if (action === "system-share") {
        const linkResult = await createSecureShareLink({
          mode: "permanent",
          tables
        });
        const shareResult = await shareWithSystem(
          linkResult.shareUrl,
          `اشتراک گذاری ${tables.length} جدول`
        );
        if (shareResult === "shared") toast.success("اشتراک گذاری انجام شد");
        else if (shareResult === "copied")
          toast.success("لینک برای اشتراک گذاری کپی شد");
        else if (shareResult === "created")
          toast.success("لینک اشتراک گذاری ایجاد شد");
        else toast.error("اشتراک گذاری لغو شد");
        setIsShareModalOpen(false);
        return;
      }

      if (action === "direct-print") {
        if (printFormContainer()) {
          toast.success("چاپ مستقیم با موفقیت شروع شد");
          setIsShareModalOpen(false);
        } else {
          toast.error("امکان چاپ در حال حاضر وجود ندارد");
        }
        return;
      }

      if (action === "email-table") {
        const linkResult = await createSecureShareLink({
          mode: "permanent",
          tables
        });
        const subject = "اشتراک گذاری جدول";
        const body = `لطفا جدول های مورد نظر را از لینک زیر مشاهده کنید:\n${linkResult.shareUrl}`;
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        setIsShareModalOpen(false);
        toast.success("پنجره ارسال ایمیل باز شد");
        return;
      }

      if (action === "email-attachment") {
        const fileResult = await createSecureShareFile({
          format: "xlsx",
          tables
        });
        const subject = "ارسال فایل خروجی جدول";
        const body = `لینک فایل اشتراکی آماده است:\n${fileResult.fileUrl}`;
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        setIsShareModalOpen(false);
        toast.success("پنجره ارسال ایمیل باز شد");
      }
    } finally {
      setIsSharing(false);
      setPendingShareAction(null);
      setShareConfigType(null);
    }
  };

  const handleShareAction = async (action: TableShareAction) => {
    const tables = collectTablesForExport();
    if (tables.length === 0) {
      toast.error("جدولی برای اشتراک گذاری پیدا نشد");
      return;
    }
    setPendingShareAction(action);
    setExportScopeContext("share");
    setIsShareModalOpen(false);
    setIsExportScopeModalOpen(true);
  };

  const handleExportScopeSubmit = async (scope: ExportScopeResult) => {
    const isExportFlow = exportScopeContext === "export";
    const processToastId = toast.loading(
      isExportFlow
        ? "در حال آماده سازی خروجی..."
        : "در حال آماده سازی اشتراک گذاری..."
    );
    const tables = await collectTablesFromScopedPreview(scope);

    if (tables.length === 0) {
      toast.error("هیچ داده ای برای پردازش انتخاب نشده است");
      toast.dismiss(processToastId);
      return;
    }

    setIsExportScopeModalOpen(false);

    if (exportScopeContext === "export") {
      if (!pendingExportFormat) {
        toast.dismiss(processToastId);
        return;
      }

      setIsExporting(true);
      try {
        await downloadTablesByFormat(pendingExportFormat, tables);
        setIsExportModalOpen(false);
        toast.success("تمام شد. همه مراحل انجام شد", { id: processToastId });
      } catch {
        toast.error("خطا در تولید فایل خروجی", { id: processToastId });
      } finally {
        setIsExporting(false);
        setPendingExportFormat(null);
      }
      return;
    }

    if (!pendingShareAction) return;

    if (pendingShareAction === "link-expiring") {
      setPendingShareTables(tables);
      setShareConfigType("expiring");
      setIsShareConfigModalOpen(true);
      toast.dismiss(processToastId);
      return;
    }
    if (pendingShareAction === "link-multi-use") {
      setPendingShareTables(tables);
      setShareConfigType("multi-use");
      setIsShareConfigModalOpen(true);
      toast.dismiss(processToastId);
      return;
    }

    try {
      await executeShareAction(pendingShareAction, tables);
      toast.success("تمام شد. همه مراحل انجام شد", {
        id: processToastId
      });
    } catch {
      toast.error("خطا در اجرای عملیات اشتراک گذاری", {
        id: processToastId
      });
    } finally {
      setPendingShareAction(null);
    }
  };

  const handleShareConfigSubmit = async (
    configValue: TableShareConfigValue
  ) => {
    if (!pendingShareAction) return;
    const tables = pendingShareTables.length
      ? pendingShareTables
      : collectTablesForExport();
    if (tables.length === 0) {
      toast.error("جدولی برای اشتراک گذاری پیدا نشد");
      return;
    }
    setIsShareConfigModalOpen(false);
    const processToastId = toast.loading("در حال آماده سازی اشتراک گذاری...");
    try {
      await executeShareAction(pendingShareAction, tables, configValue);
      toast.success("تمام شد. همه مراحل انجام شد", {
        id: processToastId
      });
    } catch {
      toast.error("خطا در ایجاد لینک اشتراک گذاری", {
        id: processToastId
      });
    } finally {
      setPendingShareTables([]);
      setPendingShareAction(null);
    }
  };

  const handleTableHeaderSortClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest("button")) return;

    const headerCell = target.closest(
      "thead tr:first-child th, thead tr:first-child td"
    ) as HTMLElement | null;
    if (!headerCell) return;

    const container = headerCell.closest("#table-container");
    if (!container) return;

    const table = container.querySelector("table");
    const sortSelect = container.querySelector(
      "#table-sort-select"
    ) as HTMLSelectElement | null;
    if (!table || !sortSelect) return;

    const headerText = normalizeCellText(headerCell);
    if (!headerText) return;

    const matchedOption = Array.from(sortSelect.options).find((option) => {
      const optionText =
        option.textContent?.replace(/\s*\((نزولی|صعودی)\)\s*$/u, "").trim() ??
        "";
      return option.value && optionText === headerText;
    });

    if (!matchedOption) return;

    const tableId = table.id || sortSelect.getAttribute("data-sort-id") || "";
    if (!tableId) return;

    sortSelect.value = matchedOption.value;
    upsertTableSort(tableId, matchedOption.value);
    setTimeout(() => refetch(), 100);
  };

  const getAdvancedSearchFields = (container: Element) => {
    const filterSelect = container.querySelector(
      "#table-filter"
    ) as HTMLSelectElement | null;
    if (filterSelect) {
      const excludedLabels = new Set([
        "لطفا یک گزینه را انتخاب کنید",
        "موردی برای انتخاب وجود ندارد",
        "تست"
      ]);
      const fieldMap = new Map<string, { id: string; name: string }>();
      Array.from(filterSelect.options).forEach((option) => {
        const id = option.value.trim();
        const name = option.textContent?.trim() ?? "";
        if (!id || excludedLabels.has(name) || id === "test") return;
        if (!fieldMap.has(id)) fieldMap.set(id, { id, name });
      });
      const fields = Array.from(fieldMap.values());
      if (fields.length > 0) return fields;
    }
    const table = container.querySelector("table");
    const headers = table?.querySelectorAll("thead tr td") ?? [];
    return Array.from(headers).map((header, index) => ({
      id: (index + 1).toString(),
      name: header.textContent?.trim() || `ستون ${index + 1}`
    }));
  };

  const updateTablePaginationItem = (
    tableId: string,
    updater: (item: TablePaginationState) => TablePaginationState
  ) => {
    if (!tableId) return;

    setTablePagination((prev) => {
      const sameItemIndex = prev.findIndex((item) => item.id === tableId);
      const currentItem: TablePaginationState =
        sameItemIndex === -1
          ? { id: tableId, pageNumber: "1", pageSize: 10 }
          : {
              ...prev[sameItemIndex],
              pageSize: prev[sameItemIndex].pageSize || 10
            };

      const nextItem = updater(currentItem);

      if (sameItemIndex === -1) return [...prev, nextItem];

      const items = [...prev];
      items[sameItemIndex] = nextItem;
      return items;
    });
  };

  const setTablePaginationFirstPage = (tableId: string) => {
    updateTablePaginationItem(tableId, (item) => ({
      ...item,
      pageNumber: "1"
    }));
  };

  const setTablePaginationPageSize = (tableId: string, pageSize: number) => {
    if (!Number.isFinite(pageSize) || pageSize <= 0) return;

    updateTablePaginationItem(tableId, (item) => ({
      ...item,
      pageNumber: "1",
      pageSize
    }));
  };

  const setTablePaginationPageNumber = (
    tableId: string,
    pageNumber: number
  ) => {
    if (!Number.isFinite(pageNumber) || pageNumber <= 0) return;

    updateTablePaginationItem(tableId, (item) => ({
      ...item,
      pageNumber: pageNumber.toString()
    }));
  };

  const handleTablePageSizeChange = (event: Event) => {
    const select = event.currentTarget as HTMLSelectElement | null;
    if (!select) return;

    const selectedPageSize = Number(select.value);
    if (!Number.isFinite(selectedPageSize) || selectedPageSize <= 0) return;

    const tableId =
      select.closest("#table-container")?.querySelector("table")?.id ?? "";
    if (!tableId) return;

    setTablePaginationPageSize(tableId, selectedPageSize);
    setTimeout(() => refetch(), 100);
  };

  const upsertTableSort = (tableId: string, sortElement: string) => {
    if (!tableId) return;
    const normalizedSortElement = sortElement.trim();
    if (!normalizedSortElement) {
      setTableSort((prev) => prev.filter((item) => item.id !== tableId));
      setTablePaginationFirstPage(tableId);
      return;
    }
    setTableSort((prev) => {
      const sameItemIndex = prev.findIndex((item) => item.id === tableId);
      if (sameItemIndex === -1) {
        return [
          ...prev,
          {
            id: tableId,
            sortElement: normalizedSortElement,
            sortDirection: "asc"
          }
        ];
      }
      const items = [...prev];
      const currentItem = items[sameItemIndex];
      const isSameColumn = currentItem.sortElement === normalizedSortElement;
      const nextDirection = isSameColumn
        ? currentItem.sortDirection === "asc"
          ? "desc"
          : "asc"
        : "asc";
      items[sameItemIndex] = {
        id: tableId,
        sortElement: normalizedSortElement,
        sortDirection: nextDirection
      };
      return items;
    });
    setTablePaginationFirstPage(tableId);
  };

  const handleTableSortSelectChange = (event: Event) => {
    const select = event.currentTarget as HTMLSelectElement | null;
    if (!select) return;
    const selectedSortElement = select.value;
    select.removeAttribute("data-previous-sort-value");
    const selectSortId = select.getAttribute("data-sort-id") ?? "";
    const tableIdFromContainer =
      select.closest("#table-container")?.querySelector("table")?.id ?? "";
    const tableId = selectSortId || tableIdFromContainer;
    if (!tableId) return;
    upsertTableSort(tableId, selectedSortElement);
    // Trigger refetch after sort change
    setTimeout(() => refetch(), 100);
  };

  const handleTableSortSelectPointerDown = (event: PointerEvent) => {
    const select = event.currentTarget as HTMLSelectElement | null;
    if (!select) return;
    const currentValue = select.value.trim();
    if (!currentValue) {
      select.removeAttribute("data-previous-sort-value");
      return;
    }
    select.setAttribute("data-previous-sort-value", currentValue);
    select.value = "";
  };

  const handleTableSortSelectBlur = (event: FocusEvent) => {
    const select = event.currentTarget as HTMLSelectElement | null;
    if (!select) return;
    const previousSortValue =
      select.getAttribute("data-previous-sort-value") ?? "";
    if (!select.value && previousSortValue) {
      select.value = previousSortValue;
    }
    select.removeAttribute("data-previous-sort-value");
  };

  const upsertTableSearch = (
    tableId: string,
    filter: string,
    searchValue: string
  ) => {
    if (!tableId) return;
    setTableSearch((prev) => {
      const sameItemIndex = prev.findIndex((item) => item.id === tableId);
      const items = [...prev];
      if (sameItemIndex === -1) {
        return [
          ...prev,
          {
            searchElement: filter,
            id: tableId,
            searchValue: searchValue.trim()
          }
        ];
      }
      items[sameItemIndex] = {
        searchElement: filter,
        id: tableId,
        searchValue
      };
      return items;
    });
    setTablePaginationFirstPage(tableId);
    // Trigger refetch after search change
    setTimeout(() => refetch(), 100);
  };

  const handleTableSearchInputKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Enter") return;
    const input = event.currentTarget as HTMLInputElement | null;
    if (!input) return;
    const searchValue = input.value.trim();
    if (!searchValue) return;
    event.preventDefault();
    const container = input.closest("#table-container");
    if (!container) return;
    const table = container.querySelector("table");
    const select = container.querySelector(
      "#table-filter"
    ) as HTMLSelectElement | null;
    upsertTableSearch(
      table ? table.id : "",
      select ? select.value : "",
      input.value
    );
  };

  // const handleAdvancedSearchSubmit = (
  //   filters: Array<{ fieldId: string; value: string }>
  // ) => {
  //   if (!currentAdvancedSearchTableId) return;
  //   // Build search query from advanced filters
  //   const searchQuery = filters.map((f) => `${f.fieldId}:${f.value}`).join("|");
  //   upsertTableSearch(currentAdvancedSearchTableId, "advanced", searchQuery);
  //   setIsAdvancedSearchOpen(false);
  // };

  const handleButtonClickTable = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const clickedButton = target.closest("button") as HTMLButtonElement | null;
    if (!clickedButton) return;
    e.preventDefault();
    const container = clickedButton.closest("#table-container");
    if (!container) return;
    const table = container.querySelector("table");
    const select = container.querySelector(
      "#table-filter"
    ) as HTMLSelectElement | null;
    const searchInput = container.querySelector(
      "#table-search-input"
    ) as HTMLInputElement;
    const sortSelect = container.querySelector(
      "#table-sort-select"
    ) as HTMLSelectElement | null;
    const tableId = table ? table.id : "";
    const filter = select ? select.value : "";
    const searchValue = searchInput ? searchInput.value : "";

    if (clickedButton.id === "table-sort-button") {
      const selectedSortElement = sortSelect?.value?.trim() ?? "";
      if (!selectedSortElement) {
        toast.error("ابتدا ستون مرتب سازی را انتخاب کنید");
        return;
      }
      upsertTableSort(tableId, selectedSortElement);
      setTimeout(() => refetch(), 100);
    } else if (
      clickedButton.id === "table-export-button" ||
      clickedButton.id === "table-share-button"
    ) {
      const tables = collectTablesForExport();
      if (tables.length === 0) {
        toast.error(
          clickedButton.id === "table-export-button"
            ? "جدولی برای خروجی پیدا نشد"
            : "جدولی برای اشتراک گذاری پیدا نشد"
        );
        return;
      }
      if (clickedButton.id === "table-export-button") {
        if (isExportModalOpen) {
          setIsExportModalOpen(false);
        } else {
          setExportTableCount(tables.length);
          setIsExportModalOpen(true);
        }
      } else {
        if (isShareModalOpen) {
          setIsShareModalOpen(false);
        } else {
          setShareTableCount(tables.length);
          setIsShareModalOpen(true);
        }
      }
    } else if (clickedButton.id === "table-search-button") {
      upsertTableSearch(tableId, filter, searchValue);
      setTimeout(() => refetch(), 100);
    } else if (clickedButton.id === "table-advanced-search-button") {
      const fields = getAdvancedSearchFields(container);
      setAdvancedSearchTableFields(fields);
      setCurrentAdvancedSearchTableId(tableId);
      setIsAdvancedSearchOpen(true);
    } else if (clickedButton.getAttribute("data-page-number")) {
      const pageNumber = Number(clickedButton.getAttribute("data-page-number"));
      if (!Number.isFinite(pageNumber) || pageNumber <= 0) return;

      setTablePaginationPageNumber(tableId, pageNumber);
      setTimeout(() => refetch(), 100);
    } else if (clickedButton.id === "table-next") {
      setTablePagination((prev) => {
        const sameItemIndex = prev.findIndex((item) => item.id === tableId);
        const items = [...prev];
        if (sameItemIndex === -1)
          return [...prev, { id: tableId, pageNumber: "2", pageSize: 10 }];
        items[sameItemIndex] = {
          ...items[sameItemIndex],
          pageNumber: (+items[sameItemIndex].pageNumber + 1).toString(),
          pageSize: items[sameItemIndex].pageSize || 10
        };
        return items;
      });
      setTimeout(() => refetch(), 100);
    } else if (clickedButton.id === "table-previous") {
      setTablePagination((prev) => {
        const sameItemIndex = prev.findIndex((item) => item.id === tableId);
        const items = [...prev];
        if (sameItemIndex === -1)
          return [...prev, { id: tableId, pageNumber: "1", pageSize: 10 }];
        items[sameItemIndex] = {
          ...items[sameItemIndex],
          ...items[sameItemIndex],
          pageNumber:
            +items[sameItemIndex].pageNumber <= 1
              ? "1"
              : (+items[sameItemIndex].pageNumber - 1).toString(),
          pageSize: items[sameItemIndex].pageSize || 10
        };
        return items;
      });
      setTimeout(() => refetch(), 100);
    } else if (clickedButton.id === "table-repeater-button") {
      if (table) {
        const lastRow = table.rows[table.rows.length - 1];
        const newRow = lastRow.cloneNode(true) as HTMLTableRowElement;
        newRow.querySelectorAll("input, textarea, select").forEach((el) => {
          if (el instanceof HTMLInputElement) {
            if (el.type === "checkbox" || el.type === "radio")
              el.checked = false;
            else el.value = "";
          } else if (
            el instanceof HTMLTextAreaElement ||
            el instanceof HTMLSelectElement
          ) {
            el.value = "";
          }
        });
        const randomGroup = Math.random().toString(36).substring(2, 12);
        newRow
          .querySelectorAll("input, textarea, select, editor")
          .forEach((el) => {
            el.setAttribute("data-group", randomGroup);
          });
        table.appendChild(newRow);
      }
    }
  };

  const handlePriceInput = (event: Event) => {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    const container = input.parentElement as HTMLDivElement;
    const priceElement = container.querySelector(
      "#price-text"
    ) as HTMLSpanElement;
    const numericValue = digitsFaToEn(input.value).replace(/[^\d.]/g, "");
    if (+numericValue < 9007199254740991) {
      input.value = numericValue;
    } else {
      input.value = "9007199254740990";
    }
    const priceInWord = `${numberToWords(+numericValue < 9007199254740991 ? numericValue : 0)} تومان`;
    priceElement.innerText = priceInWord;
  };

  const handleImagePreviewInput = (event: Event) => {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    const file = input.files?.[0];
    const container = input.parentElement as HTMLDivElement;
    const imageElement = container.querySelector(
      "#image-preview"
    ) as HTMLImageElement;
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        imageElement.src = e.target!.result as string;
      };
      reader.readAsDataURL(file);
    } else {
      imageElement.src = "";
    }
  };

  const attachEventListeners = () => {
    const formElement = formRef.current;
    if (!formElement) return;

    detachEventListeners();

    const controller = new AbortController();
    formEventsAbortControllerRef.current = controller;
    const listenerOptions = {
      signal: controller.signal
    } as AddEventListenerOptions;

    makeAllSelectsSearchable();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-searchable"
        ) {
          const target = mutation.target as HTMLElement;
          const selectElement =
            target.tagName === "SELECT"
              ? (target as HTMLSelectElement)
              : (target.querySelector("select") as HTMLSelectElement);

          if (!selectElement) return;

          const isNowSearchable =
            selectElement.getAttribute("data-searchable") !== "false";

          if (isNowSearchable) {
            // فعال کردن جستجو با تابع جدید
            selectElement.removeAttribute("data-searchable-converted");
            convertSelectToSearchableWithNoneOption(selectElement);
          } else {
            // غیرفعال کردن و برگردوندن به حالت عادی
            const wrapper = selectElement.closest(".searchable-select-wrapper");
            if (wrapper && wrapper.parentNode) {
              const parent = wrapper.parentNode;
              parent.insertBefore(selectElement, wrapper);
              parent.removeChild(wrapper);

              selectElement.style.position = "";
              selectElement.style.opacity = "";
              selectElement.style.pointerEvents = "";
              selectElement.style.width = "";
              selectElement.style.height = "";
              selectElement.style.top = "";
              selectElement.style.left = "";
              selectElement.style.display = "";
              selectElement.removeAttribute("data-searchable-converted");
            }
          }
        }
      });
    });

    observer.observe(formElement, {
      attributes: true,
      attributeFilter: ["data-searchable"],
      subtree: true
    });

    (formElement as any)._searchableObserver = observer;

    formElement.addEventListener(
      "pointerdown",
      handleDataFormActionSelectPointerDown,
      listenerOptions
    );

    formElement.addEventListener(
      "click",
      handleTableHeaderSortClick,
      listenerOptions
    );

    formElement.querySelectorAll("button").forEach((button) => {
      if (button.getAttribute("data-action")) {
        button.addEventListener(
          "click",
          handleButtonClickWithAction,
          listenerOptions
        );
      } else {
        button.addEventListener(
          "click",
          handleButtonClickTable,
          listenerOptions
        );
      }
    });

    formElement
      .querySelectorAll("select[data-pagination-size]")
      .forEach((selectElement) => {
        (selectElement as HTMLSelectElement).addEventListener(
          "change",
          handleTablePageSizeChange,
          listenerOptions
        );
      });

    formElement
      .querySelectorAll("#table-sort-select")
      .forEach((selectElement) => {
        (selectElement as HTMLSelectElement).addEventListener(
          "pointerdown",
          handleTableSortSelectPointerDown,
          listenerOptions
        );
        (selectElement as HTMLSelectElement).addEventListener(
          "change",
          handleTableSortSelectChange,
          listenerOptions
        );
        (selectElement as HTMLSelectElement).addEventListener(
          "blur",
          handleTableSortSelectBlur,
          listenerOptions
        );
      });

    formElement
      .querySelectorAll("input[data-input-type='price']")
      .forEach((priceInput) => {
        (priceInput as HTMLInputElement).addEventListener(
          "input",
          handlePriceInput,
          listenerOptions
        );
      });

    formElement
      .querySelectorAll("#table-search-input")
      .forEach((searchInputElement) => {
        (searchInputElement as HTMLInputElement).addEventListener(
          "keydown",
          handleTableSearchInputKeyDown,
          listenerOptions
        );
      });

    formElement
      .querySelectorAll("input[data-input-type='image-preview']")
      .forEach((imageInput) => {
        (imageInput as HTMLInputElement).addEventListener(
          "change",
          handleImagePreviewInput,
          listenerOptions
        );
      });

    const editorElements = formElement.querySelectorAll(
      "div[data-input-type='editor']"
    );
    editorElements.forEach((el) => {
      if (el.parentElement?.classList.contains("editor-wrapper")) return;
      const wrapper = document.createElement("div");
      wrapper.className = "editor-wrapper";
      wrapper.style.height = "100%";
      const id = el.getAttribute("id");
      el.replaceWith(wrapper);
      createRoot(wrapper).render(<EditorComponent id={id ?? ""} />);
    });
  };

  const selectConversionTracker = new WeakMap<HTMLSelectElement, boolean>();

  const convertSelectToSearchableWithNoneOption = (
    selectElement: HTMLSelectElement
  ) => {
    if (selectConversionTracker.get(selectElement)) return;
    if (selectElement.hasAttribute("data-searchable-converted")) return;

    // const isRequired = selectElement.hasAttribute("required");

    const isSearchable =
      selectElement.getAttribute("data-searchable") === "true";

    // console.log(
    //   `Converting select ${selectElement.id || "unnamed"}, isRequired: ${isRequired}`
    // );

    if (!isSearchable) return;
    selectConversionTracker.set(selectElement, true);
    selectElement.setAttribute("data-searchable-converted", "true");

    // مخفی کردن select اصلی
    selectElement.style.position = "absolute";
    selectElement.style.opacity = "0";
    selectElement.style.pointerEvents = "none";
    selectElement.style.width = "100%";
    selectElement.style.height = "100%";
    selectElement.style.top = "0";
    selectElement.style.left = "0";
    selectElement.setAttribute("data-searchable-converted", "true");

    // ساخت wrapper
    const wrapper = document.createElement("div");
    wrapper.className = "searchable-select-wrapper";
    wrapper.style.position = "relative";
    wrapper.style.display = "inline-block";
    wrapper.style.width = "100%";

    // ساخت input نمایش
    const displayInput = document.createElement("input");
    displayInput.type = "text";
    displayInput.readOnly = true;
    displayInput.placeholder = "جستجو و انتخاب کنید...";
    displayInput.style.padding = "8px 12px";
    displayInput.style.borderRadius = "6px";
    displayInput.style.cursor = "pointer";
    displayInput.style.width = "100%";
    displayInput.style.boxSizing = "border-box";

    // Arrow icon
    displayInput.style.backgroundImage = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`;
    displayInput.style.backgroundRepeat = "no-repeat";
    displayInput.style.backgroundPosition = "left 10px center";
    displayInput.style.paddingLeft = "30px";

    // تنظیم مقدار اولیه
    const updateDisplayValue = () => {
      const selectedOption = selectElement.options[selectElement.selectedIndex];
      if (
        selectedOption &&
        selectedOption.value &&
        selectedOption.value !== ""
      ) {
        displayInput.value = selectedOption.text;
      } else {
        displayInput.value = "";
      }
    };
    updateDisplayValue();

    // ساخت dropdown
    const dropdown = document.createElement("div");
    dropdown.className = "searchable-dropdown";
    dropdown.style.display = "none";
    dropdown.style.position = "absolute";
    dropdown.style.top = "100%";
    dropdown.style.left = "0";
    dropdown.style.right = "0";
    dropdown.style.zIndex = "9999";
    dropdown.style.backgroundColor = "white";
    dropdown.style.border = "1px solid #e5e7eb";
    dropdown.style.borderRadius = "6px";
    dropdown.style.boxShadow = "0 10px 25px rgba(0,0,0,0.15)";
    dropdown.style.marginTop = "4px";
    dropdown.style.direction = "rtl";
    dropdown.style.textAlign = "right";
    dropdown.style.maxHeight = "300px";

    // Input جستجو
    const searchInput = document.createElement("input");
    searchInput.type = "text";
    searchInput.placeholder = "جستجو...";
    searchInput.style.width = "100%";
    searchInput.style.padding = "10px 12px";
    searchInput.style.border = "none";
    searchInput.style.borderBottom = "1px solid #e5e7eb";
    searchInput.style.boxSizing = "border-box";
    searchInput.style.outline = "none";
    searchInput.style.direction = "rtl";
    searchInput.style.textAlign = "right";

    // لیست گزینه‌ها
    const optionsContainer = document.createElement("div");
    optionsContainer.style.maxHeight = "200px";
    optionsContainer.style.overflowY = "auto";
    optionsContainer.style.padding = "4px 0";

    // رندر گزینه‌ها
    const renderOptions = (filterText = "") => {
      const isRequired = selectElement.hasAttribute("required");
      console.log(
        "renderOptions",
        selectElement.id,
        selectElement.hasAttribute("required")
      );
      optionsContainer.innerHTML = "";
      const options = Array.from(selectElement.options);

      // فیلتر کردن گزینه‌ها بر اساس متن جستجو
      const filteredOptions = options.filter((opt) => {
        if (!opt.value || opt.value === "") return false;
        if (filterText === "") return true;
        return opt.text.toLowerCase().includes(filterText.toLowerCase());
      });

      // اضافه کردن گزینه "هیچکدام" فقط اگر:
      // 1. required نباشد
      // 2. متن جستجو خالی باشد (تا در حین جستجو نمایش داده نشود)
      if (!isRequired && filterText === "") {
        const noneOptionDiv = document.createElement("div");
        noneOptionDiv.style.padding = "8px 12px";
        noneOptionDiv.style.cursor = "pointer";
        noneOptionDiv.style.fontSize = "14px";
        noneOptionDiv.style.transition = "background-color 0.2s";
        noneOptionDiv.style.textAlign = "right";
        noneOptionDiv.style.color = "#6b7280";
        noneOptionDiv.style.borderBottom = "1px solid #e5e7eb";
        noneOptionDiv.style.marginBottom = "4px";
        noneOptionDiv.style.fontWeight = "normal";

        noneOptionDiv.innerHTML =
          '<span style="opacity: 0.7;">✕</span> هیچکدام';

        // اگر مقدار فعلی خالی است، هایلایت کن
        if (!selectElement.value || selectElement.value === "") {
          noneOptionDiv.style.backgroundColor = "#eff6ff";
          noneOptionDiv.style.fontWeight = "bold";
        }

        noneOptionDiv.addEventListener("mouseenter", () => {
          if (selectElement.value !== "") {
            noneOptionDiv.style.backgroundColor = "#f9fafb";
          }
        });

        noneOptionDiv.addEventListener("mouseleave", () => {
          if (selectElement.value !== "") {
            noneOptionDiv.style.backgroundColor = "";
          }
          if (!selectElement.value || selectElement.value === "") {
            noneOptionDiv.style.backgroundColor = "#eff6ff";
          }
        });

        noneOptionDiv.addEventListener("click", (e) => {
          e.stopPropagation();
          // مقدار select رو خالی کن
          selectElement.value = "";
          updateDisplayValue();

          // Fire events
          selectElement.dispatchEvent(new Event("change", { bubbles: true }));
          selectElement.dispatchEvent(new Event("input", { bubbles: true }));

          // بستن dropdown
          dropdown.style.display = "none";
          searchInput.value = "";
        });

        optionsContainer.appendChild(noneOptionDiv);
      }

      if (filteredOptions.length === 0) {
        const noResult = document.createElement("div");
        noResult.textContent = "نتیجه‌ای یافت نشد";
        noResult.style.padding = "12px";
        noResult.style.color = "#9ca3af";
        noResult.style.textAlign = "center";
        noResult.style.fontSize = "14px";
        optionsContainer.appendChild(noResult);
        return;
      }

      filteredOptions.forEach((option) => {
        const optionDiv = document.createElement("div");
        optionDiv.style.padding = "8px 12px";
        optionDiv.style.cursor = option.disabled ? "default" : "pointer";
        optionDiv.style.fontSize = "14px";
        optionDiv.style.transition = "background-color 0.2s";
        optionDiv.style.textAlign = "right";

        // هایلایت متن جستجو شده
        if (
          filterText &&
          option.text &&
          option.text.toLowerCase().includes(filterText.toLowerCase())
        ) {
          const regex = new RegExp(
            `(${filterText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
            "gi"
          );
          optionDiv.innerHTML = option.text.replace(
            regex,
            '<mark style="background-color: #fef08a; padding: 0 2px; border-radius: 2px;">$1</mark>'
          );
        } else {
          optionDiv.textContent = option.text;
        }

        // انتخاب شده
        if (option.value === selectElement.value) {
          optionDiv.style.backgroundColor = "#eff6ff";
          optionDiv.style.fontWeight = "bold";
        }

        if (!option.disabled) {
          optionDiv.addEventListener("mouseenter", () => {
            if (option.value !== selectElement.value) {
              optionDiv.style.backgroundColor = "#f9fafb";
            }
          });

          optionDiv.addEventListener("mouseleave", () => {
            if (option.value !== selectElement.value) {
              optionDiv.style.backgroundColor = "";
            }
          });

          optionDiv.addEventListener("click", (e) => {
            e.stopPropagation();
            selectElement.value = option.value;
            updateDisplayValue();

            // Fire events
            selectElement.dispatchEvent(new Event("change", { bubbles: true }));
            selectElement.dispatchEvent(new Event("input", { bubbles: true }));

            // بستن dropdown
            dropdown.style.display = "none";
            searchInput.value = "";
          });
        }

        optionsContainer.appendChild(optionDiv);
      });
    };

    // Event listeners
    const toggleDropdown = (e: Event) => {
      console.log("toggleDropdown");
      e.stopPropagation();
      const isOpen = dropdown.style.display === "block";

      // بستن همه dropdownهای دیگه
      document.querySelectorAll(".searchable-dropdown").forEach((d) => {
        (d as HTMLElement).style.display = "none";
      });

      if (isOpen) {
        dropdown.style.display = "none";
      } else {
        dropdown.style.display = "block";
        renderOptions();
        setTimeout(() => {
          searchInput.focus();
        }, 50);
      }
    };

    displayInput.addEventListener("click", toggleDropdown);

    searchInput.addEventListener("input", (e) => {
      renderOptions((e.target as HTMLInputElement).value);
    });

    searchInput.addEventListener("click", (e) => {
      e.stopPropagation();
    });

    // بستن با کلیک خارج
    const handleClickOutside = (e: MouseEvent) => {
      if (!wrapper.contains(e.target as Node)) {
        dropdown.style.display = "none";
        searchInput.value = "";
      }
    };
    document.addEventListener("click", handleClickOutside);

    // Cleanup function
    (wrapper as any)._cleanup = () => {
      document.removeEventListener("click", handleClickOutside);
    };

    // مونتاژ
    dropdown.appendChild(searchInput);
    dropdown.appendChild(optionsContainer);
    wrapper.appendChild(displayInput);
    wrapper.appendChild(dropdown);

    // جایگذاری
    const parent = selectElement.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, selectElement);
      wrapper.appendChild(selectElement);
    }

    // رندر اولیه
    renderOptions();
  };

  attachEventListenersRef.current = attachEventListeners;
  // const convertSelectToSearchable = (selectElement: HTMLSelectElement) => {
  //   if (selectElement.hasAttribute("data-searchable-converted")) return;
  //   const isSearchable =
  //     selectElement.getAttribute("data-searchable") === "true";
  //   if (!isSearchable) return;
  //   if (selectElement.hasAttribute("data-searchable-converted")) return;

  //   // مخفی کردن select اصلی با hidden (نه display:none)
  //   selectElement.style.position = "absolute";
  //   selectElement.style.opacity = "0";
  //   selectElement.style.pointerEvents = "none";
  //   selectElement.style.width = "100%";
  //   selectElement.style.height = "100%";
  //   selectElement.style.top = "0";
  //   selectElement.style.left = "0";
  //   selectElement.setAttribute("data-searchable-converted", "true");

  //   // ساخت wrapper
  //   const wrapper = document.createElement("div");
  //   wrapper.className = "searchable-select-wrapper";
  //   wrapper.style.position = "relative";
  //   wrapper.style.display = "inline-block";

  //   // ساخت input نمایش
  //   const displayInput = document.createElement("input");
  //   displayInput.type = "text";
  //   displayInput.readOnly = true;
  //   displayInput.placeholder = "جستجو و انتخاب کنید...";
  //   displayInput.style.padding = "8px 12px";
  //   displayInput.style.borderRadius = "6px";
  //   displayInput.style.cursor = "pointer";

  //   // Arrow icon
  //   displayInput.style.backgroundImage = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`;
  //   displayInput.style.backgroundRepeat = "no-repeat";
  //   displayInput.style.backgroundPosition = "left 10px center";
  //   displayInput.style.paddingLeft = "30px";

  //   // تنظیم مقدار اولیه
  //   const updateDisplayValue = () => {
  //     const selectedOption = selectElement.options[selectElement.selectedIndex];
  //     if (selectedOption && selectedOption.value) {
  //       displayInput.value = selectedOption.text;
  //     } else {
  //       displayInput.value = "";
  //     }
  //   };
  //   updateDisplayValue();

  //   // ساخت dropdown
  //   const dropdown = document.createElement("div");
  //   dropdown.className = "searchable-dropdown";
  //   dropdown.style.display = "none";
  //   dropdown.style.position = "absolute";
  //   dropdown.style.top = "100%";
  //   dropdown.style.left = "0";
  //   dropdown.style.right = "0";
  //   dropdown.style.zIndex = "9999";
  //   dropdown.style.backgroundColor = "white";
  //   dropdown.style.border = "1px solid #e5e7eb";
  //   dropdown.style.borderRadius = "6px";
  //   dropdown.style.boxShadow = "0 10px 25px rgba(0,0,0,0.15)";
  //   dropdown.style.marginTop = "4px";
  //   dropdown.style.direction = "rtl";
  //   dropdown.style.textAlign = "right";
  //   dropdown.style.maxHeight = "300px";
  //   dropdown.style.display = "none";

  //   // Input جستجو
  //   const searchInput = document.createElement("input");
  //   searchInput.type = "text";
  //   searchInput.placeholder = "جستجو...";
  //   searchInput.style.width = "100%";
  //   searchInput.style.padding = "10px 12px";
  //   searchInput.style.border = "none";
  //   searchInput.style.borderBottom = "1px solid #e5e7eb";
  //   searchInput.style.boxSizing = "border-box";
  //   searchInput.style.outline = "none";
  //   searchInput.style.direction = "rtl";
  //   searchInput.style.textAlign = "right";

  //   // لیست گزینه‌ها
  //   const optionsContainer = document.createElement("div");
  //   optionsContainer.style.maxHeight = "200px";
  //   optionsContainer.style.overflowY = "auto";
  //   optionsContainer.style.padding = "4px 0";

  //   // رندر گزینه‌ها
  //   const renderOptions = (filterText = "") => {
  //     optionsContainer.innerHTML = "";
  //     const options = Array.from(selectElement.options);

  //     const filteredOptions = options.filter((opt) => {
  //       if (!opt.value || opt.value === "") return false;
  //       if (filterText === "") return true;
  //       return opt.text.toLowerCase().includes(filterText.toLowerCase());
  //     });

  //     if (filteredOptions.length === 0) {
  //       const noResult = document.createElement("div");
  //       noResult.textContent = "نتیجه‌ای یافت نشد";
  //       noResult.style.padding = "12px";
  //       noResult.style.color = "#9ca3af";
  //       noResult.style.textAlign = "center";
  //       noResult.style.fontSize = "14px";
  //       optionsContainer.appendChild(noResult);
  //       return;
  //     }

  //     filteredOptions.forEach((option) => {
  //       const optionDiv = document.createElement("div");
  //       optionDiv.style.padding = "8px 12px";
  //       optionDiv.style.cursor = "pointer";
  //       optionDiv.style.fontSize = "14px";
  //       optionDiv.style.transition = "background-color 0.2s";
  //       optionDiv.style.textAlign = "right";

  //       // هایلایت متن جستجو شده
  //       if (
  //         filterText &&
  //         option.text.toLowerCase().includes(filterText.toLowerCase())
  //       ) {
  //         const regex = new RegExp(
  //           `(${filterText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
  //           "gi"
  //         );
  //         optionDiv.innerHTML = option.text.replace(
  //           regex,
  //           '<mark style="background-color: #fef08a; padding: 0 2px; border-radius: 2px;">$1</mark>'
  //         );
  //       } else {
  //         optionDiv.textContent = option.text;
  //       }

  //       // انتخاب شده
  //       if (option.value === selectElement.value) {
  //         optionDiv.style.backgroundColor = "#eff6ff";
  //         optionDiv.style.fontWeight = "bold";
  //       }

  //       optionDiv.addEventListener("mouseenter", () => {
  //         if (option.value !== selectElement.value) {
  //           optionDiv.style.backgroundColor = "#f9fafb";
  //         }
  //       });

  //       optionDiv.addEventListener("mouseleave", () => {
  //         if (option.value !== selectElement.value) {
  //           optionDiv.style.backgroundColor = "";
  //         }
  //       });

  //       optionDiv.addEventListener("click", (e) => {
  //         e.stopPropagation();
  //         selectElement.value = option.value;
  //         updateDisplayValue();

  //         // Fire events
  //         selectElement.dispatchEvent(new Event("change", { bubbles: true }));
  //         selectElement.dispatchEvent(new Event("input", { bubbles: true }));

  //         // بستن dropdown
  //         dropdown.style.display = "none";
  //         searchInput.value = "";
  //       });

  //       optionsContainer.appendChild(optionDiv);
  //     });
  //   };

  //   // Event listeners
  //   const toggleDropdown = (e: Event) => {
  //     e.stopPropagation();
  //     const isOpen = dropdown.style.display === "block";

  //     // بستن همه dropdownهای دیگه
  //     document.querySelectorAll(".searchable-dropdown").forEach((d) => {
  //       (d as HTMLElement).style.display = "none";
  //     });

  //     if (isOpen) {
  //       dropdown.style.display = "none";
  //     } else {
  //       dropdown.style.display = "block";
  //       renderOptions();
  //       setTimeout(() => {
  //         searchInput.focus();
  //       }, 50);
  //     }
  //   };

  //   displayInput.addEventListener("click", toggleDropdown);

  //   searchInput.addEventListener("input", (e) => {
  //     renderOptions((e.target as HTMLInputElement).value);
  //   });

  //   searchInput.addEventListener("click", (e) => {
  //     e.stopPropagation();
  //   });

  //   // بستن با کلیک خارج
  //   const handleClickOutside = (e: MouseEvent) => {
  //     if (!wrapper.contains(e.target as Node)) {
  //       dropdown.style.display = "none";
  //       searchInput.value = "";
  //     }
  //   };
  //   document.addEventListener("click", handleClickOutside);

  //   // Cleanup function
  //   (wrapper as any)._cleanup = () => {
  //     document.removeEventListener("click", handleClickOutside);
  //   };

  //   // مونتاژ
  //   dropdown.appendChild(searchInput);
  //   dropdown.appendChild(optionsContainer);
  //   wrapper.appendChild(displayInput);
  //   wrapper.appendChild(dropdown);

  //   // جایگذاری
  //   const parent = selectElement.parentNode;
  //   if (parent) {
  //     parent.insertBefore(wrapper, selectElement);
  //     wrapper.appendChild(selectElement);
  //   }

  //   // رندر اولیه
  //   renderOptions();
  // };

  const makeAllSelectsSearchable = () => {
    const formElement = formRef.current;
    if (!formElement) return;

    const selectElements = formElement.querySelectorAll(
      "select:not([data-searchable-converted])"
    );
    selectElements.forEach((select) => {
      // به جای convertSelectToSearchable، از تابع جدید استفاده کن
      convertSelectToSearchableWithNoneOption(select as HTMLSelectElement);
    });
  };

  const detachEventListeners = () => {
    formEventsAbortControllerRef.current?.abort();
    formEventsAbortControllerRef.current = null;
  };

  useEffect(() => {
    const formElement = formRef.current;
    if (!formElement) return;

    const observer = (formElement as any)._searchableObserver;
    if (observer) {
      observer.disconnect();
      delete (formElement as any)._searchableObserver;
    }

    // Avoid React re-injecting HTML on unrelated rerenders (e.g. modal open/close)
    formElement.innerHTML = formBody;

    // Small delay to ensure DOM is ready
    const timer = window.setTimeout(
      () => attachEventListenersRef.current(),
      100
    );
    return () => {
      window.clearTimeout(timer);
      detachEventListeners();
    };
  }, [form?.data]);

  useEffect(() => {
    window.jalaliDatepicker?.startWatch({
      minDate: "attr",
      maxDate: "attr",
      time: true
    });
  }, []);

  if (isLoading) return <Loading />;
  if (!form) return <EmptyState />;

  const checkAttribute = (str: string): boolean => {
    const regex = /data-isfullscreen="false"/i;
    return regex.test(str);
  };

  const result = checkAttribute(formBody);
  const containsIsmodal = (text: string) =>
    text.includes('data-ismodal="true"');

  const formContentClassName = `prose-preview ${
    result
      ? "-translate-y-[37%] scale-[0.3] rounded-xl !shadow-2xl sm:-translate-y-1/4 sm:scale-50 md:-translate-y-[10%] md:scale-75 lg:translate-y-0 lg:scale-100 2xl:translate-y-[15%] 2xl:scale-[1.2]"
      : "w-full"
  }`;

  return (
    <>
      {nodeSequence && nodeSequence.data && nodeSequence.data.length > 0 && (
        <NodeSequenceCircles sequences={nodeSequence.data} />
      )}
      {containsIsmodal(formBody) ? (
        containsIsmodal(lastFormData) ? (
          <>
            <div></div>

            <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/70">
              <div className="relative m-4 my-8 w-fit max-w-5xl rounded-lg bg-white shadow-lg">
                <div className="max-h-[calc(100vh-120px)] overflow-y-auto p-6">
                  <div ref={formRef} className={formContentClassName} />
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div>
              <div
                dangerouslySetInnerHTML={{ __html: lastFormData }}
                className="[&_.tooltiptext]:hidden"
              />
            </div>

            <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/70">
              <div className="relative m-4 my-8 w-fit max-w-5xl rounded-lg bg-white shadow-lg">
                <div className="max-h-[calc(100vh-120px)] overflow-y-auto p-6">
                  <div ref={formRef} className={formContentClassName} />
                </div>
              </div>
            </div>
          </>
        )
      ) : (
        <div className="flex justify-center" id="formContainer">
          <div ref={formRef} className={formContentClassName} />
        </div>
      )}

      <TableExportModal
        isOpen={isExportModalOpen}
        onOpenChange={setIsExportModalOpen}
        onSelect={handleExport}
        isLoading={isExporting}
        tableCount={exportTableCount}
      />

      <TableShareModal
        isOpen={isShareModalOpen}
        onOpenChange={setIsShareModalOpen}
        onSelect={handleShareAction}
        isLoading={isSharing}
        tableCount={shareTableCount}
      />

      <TableExportScopeModal
        isOpen={isExportScopeModalOpen}
        isLoading={isExporting || isSharing}
        contextTitle={
          exportScopeContext === "export"
            ? "تنظیمات خروجی"
            : "تنظیمات اشتراک گذاری"
        }
        tables={collectTableScopeItems()}
        onOpenChange={setIsExportScopeModalOpen}
        onSubmit={handleExportScopeSubmit}
      />

      <TableShareConfigModal
        isOpen={isShareConfigModalOpen}
        type={shareConfigType}
        isLoading={false} // ✅ اضافه کن
        onOpenChange={setIsShareConfigModalOpen}
        onSubmit={handleShareConfigSubmit}
      />

      <PageAdvancedSearchModal
        isOpen={isAdvancedSearchOpen}
        onOpenChange={setIsAdvancedSearchOpen}
        fields={advancedSearchTableFields}
        onSearch={(query: string) => {
          if (currentAdvancedSearchTableId) {
            upsertTableSearch(currentAdvancedSearchTableId, "advanced", query);
            setIsAdvancedSearchOpen(false);
            setTimeout(() => refetch(), 100);
          }
        }}
      />
    </>
  );
};

const Loading = () => (
  <div className="flex h-[calc(100vh/2)] w-full items-center justify-center">
    <MoonLoader color="#0099A5" size={50} />
  </div>
);

const EmptyState = () => (
  <div className="flex h-32 w-full items-center justify-center bg-white shadow-md">
    <p className="text-slate-500">فرمی پیدا نشد</p>
  </div>
);

export default FormFinal;
